import { useEffect, useRef, useState, type ChangeEvent } from "react";
import type { PhotoRow } from "@garden/types/db";
import { supabase } from "../auth";
import {
  createPhotoTag,
  listPhotosForSubject,
  plantHasAnyPhoto,
} from "../db/photoTags";
import { markPhotoTagged } from "../db/photos";
import { uploadPhotoFile } from "../lib/photoUpload";
import { usePhotoUrl } from "../lib/photoServe";
import { useCurrentGardenId } from "../lib/useCurrentGarden";
import "./PlantPhotosTab.css";

interface PlantPhotosTabProps {
  plantId: string;
}

type AddState =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "error"; message: string };

export function PlantPhotosTab({ plantId }: PlantPhotosTabProps) {
  const [photos, setPhotos] = useState<PhotoRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<PhotoRow | null>(null);
  const [addState, setAddState] = useState<AddState>({ kind: "idle" });
  const inputRef = useRef<HTMLInputElement>(null);
  const gardenId = useCurrentGardenId();

  const reload = () => {
    if (!supabase) return;
    listPhotosForSubject(supabase, "plant", plantId)
      .then(setPhotos)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  };

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    listPhotosForSubject(supabase, "plant", plantId)
      .then((rows) => {
        if (!active) return;
        setPhotos(rows);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      active = false;
    };
  }, [plantId]);

  const onPick = () => {
    if (addState.kind === "uploading") return;
    inputRef.current?.click();
  };

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!supabase) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!gardenId) {
      setAddState({ kind: "error", message: "No active garden." });
      return;
    }
    setAddState({ kind: "uploading" });
    try {
      const { row } = await uploadPhotoFile(file, gardenId);
      const isPrimary = !(await plantHasAnyPhoto(supabase, plantId));
      await createPhotoTag(supabase, {
        photo_id: row.id,
        subject_type: "plant",
        subject_id: plantId,
        is_primary: isPrimary,
      });
      await markPhotoTagged(supabase, row.id);
      setAddState({ kind: "idle" });
      reload();
    } catch (err) {
      setAddState({
        kind: "error",
        message: err instanceof Error ? err.message : "Upload failed",
      });
    }
  };

  const addButton = (
    <button
      type="button"
      className="PlantPhotosTab__add"
      onClick={onPick}
      disabled={addState.kind === "uploading"}
      title={addState.kind === "error" ? addState.message : undefined}
    >
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <circle cx="8" cy="9" r="3.5" />
        <path d="M3 6h2l1.5-2h3L11 6h2v8H3V6z" />
      </svg>
      {addState.kind === "uploading" ? "Uploading…" : "Add photo"}
    </button>
  );

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      capture="environment"
      onChange={onChange}
      style={{ display: "none" }}
    />
  );

  if (error) return <div className="PlantPhotosTab__error">{error}</div>;
  if (photos === null) return <div className="PlantPhotosTab__loading">Loading…</div>;

  if (photos.length === 0) {
    return (
      <div className="PlantPhotosTab__empty">
        {addButton}
        {fileInput}
        <p>No photos tagged to this plant yet.</p>
        <p className="PlantPhotosTab__emptyHint">
          Or capture from <code>/today</code> and tag from <code>/tag</code>.
        </p>
        {addState.kind === "error" && (
          <p className="PlantPhotosTab__addErr">{addState.message}</p>
        )}
      </div>
    );
  }

  return (
    <div className="PlantPhotosTab">
      <div className="PlantPhotosTab__head">
        {addButton}
        {fileInput}
      </div>
      <div className="PlantPhotosTab__grid">
        {photos.map((p) => (
          <button
            key={p.id}
            type="button"
            className="PlantPhotosTab__cell"
            onClick={() => setViewer(p)}
            aria-label={`Open photo from ${p.taken_date ?? "unknown date"}`}
          >
            <PhotoThumb r2Key={p.r2_key} />
            {p.taken_date && (
              <span className="PlantPhotosTab__date">{formatDate(p.taken_date)}</span>
            )}
          </button>
        ))}
      </div>

      {addState.kind === "error" && (
        <p className="PlantPhotosTab__addErr">{addState.message}</p>
      )}

      {viewer && (
        <PhotoViewer photo={viewer} onClose={() => setViewer(null)} />
      )}
    </div>
  );
}

function PhotoThumb({ r2Key }: { r2Key: string }) {
  const { url, error } = usePhotoUrl(r2Key);
  if (error) return <div className="PlantPhotosTab__thumbErr">!</div>;
  if (!url) return <div className="PlantPhotosTab__thumbLoading" />;
  return <img className="PlantPhotosTab__thumb" src={url} alt="" />;
}

function PhotoViewer({ photo, onClose }: { photo: PhotoRow; onClose: () => void }) {
  const { url } = usePhotoUrl(photo.r2_key);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="PhotoViewer" onClick={onClose} role="dialog">
      <button
        type="button"
        className="PhotoViewer__close"
        onClick={onClose}
        aria-label="Close viewer"
      >
        ✕
      </button>
      {url && <img className="PhotoViewer__img" src={url} alt="" />}
      {photo.taken_date && (
        <div className="PhotoViewer__meta">{formatDate(photo.taken_date)}</div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}
